/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import {
    AfterContentInit,
    Component,
    ContentChildren,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    Output,
    QueryList
} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';

import {Expand} from '../../utils/expand/providers/expand';
import {LoadingListener} from '../../utils/loading/loading-listener';

import {DatagridCell} from './datagrid-cell';
import {DatagridHideableColumn} from './datagrid-hideable-column';
import {ExpandableRowsCount} from './providers/global-expandable-rows';
import {HideableColumnService} from './providers/hideable-column.service';
import {RowActionService} from './providers/row-action-service';
import {Selection, SelectionType} from './providers/selection';


let /** @type {?} */ nbRow: number = 0;
export class DatagridRow implements AfterContentInit {
public id: string;
public SELECTION_TYPE = SelectionType;
private readonly ENTER_KEY_CODE = 13;
private readonly SPACE_KEY_CODE = 32;
/**
 * Model of the row, to use for selection
 */
item: any;

     role: string;
/**
 * @param {?} selection
 * @param {?} rowActionService
 * @param {?} globalExpandable
 * @param {?} expand
 * @param {?} hideableColumnService
 */
constructor(public selection: Selection,
public rowActionService: RowActionService,
public globalExpandable: ExpandableRowsCount,
public expand: Expand,
public hideableColumnService: HideableColumnService) {
        this.id = "clr-dg-row" + (nbRow++);
        this.role = selection.rowSelectionMode ? "button" : null;
    }
private _selected = false;
/**
 * Indicates if the row is selected
 * @return {?}
 */
public get selected() {
        if (this.selection.selectionType === SelectionType.None) {
            return this._selected;
        } else {
            return this.selection.isSelected(this.item);
        }
    }
/**
 * @param {?} value
 * @return {?}
 */
public set selected(value: boolean) {
        if (this.selection.selectionType === SelectionType.None) {
            this._selected = value;
        } else {
            this.selection.setSelected(this.item, value);
        }
    }

     selectedChanged = new EventEmitter<boolean>(false);
/**
 * @param {?=} selected
 * @return {?}
 */
public toggle(selected = !this.selected) {
        if (selected !== this.selected) {
            this.selected = selected;
            this.selectedChanged.emit(selected);
        }
    }
/**
 * @return {?}
 */
public get expanded() {
        return this.expand.expanded;
    }
/**
 * @param {?} value
 * @return {?}
 */
public set expanded(value: boolean) {
        this.expand.expanded = value;
    }

     expandedChange = new EventEmitter<boolean>(false);
/**
 * @return {?}
 */
public toggleExpand() {
        if (this.expand.expandable) {
            this.expanded = !this.expanded;
            this.expandedChange.emit(this.expanded);
        }
    }
/**
 * @return {?}
 */
public toggleSelection() {
        if (!this.selection.rowSelectionMode) {
            return;
        }

        switch (this.selection.selectionType) {
            case SelectionType.None:
                break;
            case SelectionType.Single:
                this.selection.currentSingle = this.item;
                break;
            case SelectionType.Multi:
                this.toggle();
                break;
            default:
                break;
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
public keypress(event: KeyboardEvent) {
        if (!this.selection.rowSelectionMode) {
            return;
        }

        // Check to see if space or enter were pressed
        if (event.keyCode === this.ENTER_KEY_CODE || event.keyCode === this.SPACE_KEY_CODE) {
            // Prevent the default action to stop scrolling when space is pressed
            event.preventDefault();
            this.toggleSelection();
        }
    }
private subscription: Subscription;
/**
 * **
 * property dgCells
 * 
 * \@description 
 * A Query List of the Datagrid cells in this row.
 * 
 */
dgCells: QueryList<DatagridCell>;
/**
 * @return {?}
 */
ngAfterContentInit() {
        // Make sure things get started
        const /** @type {?} */ columnsList = this.hideableColumnService.getColumns();
        this.updateCellsForColumns(columnsList);

        // Triggered when the Cells list changes per row-renderer
        this.dgCells.changes.subscribe((cellList) => {
            const /** @type {?} */ columnList = this.hideableColumnService.getColumns();
            if (cellList.length === columnList.length) {
                this.updateCellsForColumns(columnList);
            }
        });

        // Used to set things up the first time but only after all the columns are ready.
        this.subscription = this.hideableColumnService.columnListChange.subscribe((columnList) => {
            // Prevents cell updates when cols and cells array are not aligned - only seems to run on init / first time.
            if (columnList.length === this.dgCells.length) {
                this.updateCellsForColumns(columnList);
            }
        });
    }
/**
 * *******
 * \@function updateCellsForColumns
 * 
 * \@description 
 * 1. Maps the new columnListChange to the dgCells list by index
 * 2. Sets the hidden state on the cell
 * Take a Column list and use index to access the columns for hideable properties.
 * 
 * @param {?} columnList
 * @return {?}
 */
public updateCellsForColumns(columnList: DatagridHideableColumn[]) {
        // Map cells to columns with Array.index
        this.dgCells.forEach((cell, index) => {
            const /** @type {?} */ currentColumn = columnList[index];  // Accounts for null space.
            if (currentColumn) {
                cell.id = currentColumn.id;
            }
        });
    }
/**
 * @return {?}
 */
ngOnDestroy() {
        this.subscription.unsubscribe();
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: "clr-dg-row",
    template: `
        <div class="datagrid-row-master datagrid-row-flex">
            <clr-dg-cell *ngIf="selection.selectionType === SELECTION_TYPE.Multi"
                         class="datagrid-select datagrid-fixed-column">
                <clr-checkbox [clrChecked]="selected" (clrCheckedChange)="toggle($event)"></clr-checkbox>
            </clr-dg-cell>
            <clr-dg-cell *ngIf="selection.selectionType === SELECTION_TYPE.Single"
                         class="datagrid-select datagrid-fixed-column">
                <div class="radio">
                    <input type="radio" [id]="id" [name]="selection.id + '-radio'" [value]="item"
                           [(ngModel)]="selection.currentSingle">
                    <label for="{{id}}"></label>
                </div>
            </clr-dg-cell>
            <clr-dg-cell *ngIf="rowActionService.hasActionableRow"
                         class="datagrid-row-actions datagrid-fixed-column">
                <ng-content select="clr-dg-action-overflow"></ng-content>
            </clr-dg-cell>
            <clr-dg-cell *ngIf="globalExpandable.hasExpandableRow"
                         class="datagrid-expandable-caret datagrid-fixed-column">
                <ng-container *ngIf="expand.expandable">
                    <button (click)="toggleExpand()" *ngIf="!expand.loading" type="button" class="datagrid-expandable-caret-button">
                        <clr-icon shape="caret" [attr.dir]="expand.expanded?'down':'right'" class="datagrid-expandable-caret-icon"></clr-icon>
                    </button>
                    <div class="spinner spinner-sm" *ngIf="expand.loading"></div>
                </ng-container>
            </clr-dg-cell>
            <ng-content *ngIf="!expand.replace || !expand.expanded || expand.loading"></ng-content>

            <ng-template *ngIf="expand.replace && expand.expanded && !expand.loading"
                         [ngTemplateOutlet]="detail"></ng-template>
        </div>

        <ng-template *ngIf="!expand.replace && expand.expanded && !expand.loading"
                     [ngTemplateOutlet]="detail"></ng-template>

        <!-- 
            We need the "project into template" hack because we need this in 2 different places
            depending on whether the details replace the row or not.
        -->
        <ng-template #detail>
            <ng-content select="clr-dg-row-detail"></ng-content>
        </ng-template>
    `,
    host: {
        "[class.datagrid-row]": "true",
        "[class.datagrid-selected]": "selected",
        "[attr.tabindex]": "selection.rowSelectionMode ? 0 : null"
    },
    providers: [Expand, {provide: LoadingListener, useExisting: Expand}]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Selection, },
{type: RowActionService, },
{type: ExpandableRowsCount, },
{type: Expand, },
{type: HideableColumnService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'item': [{ type: Input, args: ["clrDgItem", ] },],
'role': [{ type: HostBinding, args: ["attr.role", ] },],
'selected': [{ type: Input, args: ["clrDgSelected", ] },],
'selectedChanged': [{ type: Output, args: ["clrDgSelectedChange", ] },],
'expanded': [{ type: Input, args: ["clrDgExpanded", ] },],
'expandedChange': [{ type: Output, args: ["clrDgExpandedChange", ] },],
'toggleSelection': [{ type: HostListener, args: ["click", ] },],
'keypress': [{ type: HostListener, args: ["keypress", ["$event"], ] },],
'dgCells': [{ type: ContentChildren, args: [DatagridCell, ] },],
};
}

function DatagridRow_tsickle_Closure_declarations() {
/** @type {?} */
DatagridRow.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DatagridRow.ctorParameters;
/** @type {?} */
DatagridRow.propDecorators;
/** @type {?} */
DatagridRow.prototype.id;
/** @type {?} */
DatagridRow.prototype.SELECTION_TYPE;
/** @type {?} */
DatagridRow.prototype.ENTER_KEY_CODE;
/** @type {?} */
DatagridRow.prototype.SPACE_KEY_CODE;
/**
 * Model of the row, to use for selection
 * @type {?}
 */
DatagridRow.prototype.item;
/** @type {?} */
DatagridRow.prototype.role;
/** @type {?} */
DatagridRow.prototype._selected;
/** @type {?} */
DatagridRow.prototype.selectedChanged;
/** @type {?} */
DatagridRow.prototype.expandedChange;
/** @type {?} */
DatagridRow.prototype.subscription;
/**
 * **
 * property dgCells
 * 
 * \@description 
 * A Query List of the Datagrid cells in this row.
 * 
 * @type {?}
 */
DatagridRow.prototype.dgCells;
/** @type {?} */
DatagridRow.prototype.selection;
/** @type {?} */
DatagridRow.prototype.rowActionService;
/** @type {?} */
DatagridRow.prototype.globalExpandable;
/** @type {?} */
DatagridRow.prototype.expand;
/** @type {?} */
DatagridRow.prototype.hideableColumnService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
